import { BlockSelectField } from '@common/components/FormElements';
import { useIsDesktop } from '@common/hooks/useIsDesktop';
import { useTranslations } from '@lang';
import { TimeSlotOptionHOC } from '@features/spa/pages/SpaDay/SpaPackageModal/SpaPackageAvailability/DateTimeSelector/TimeslotSelector/TimeSlotOptionHOC';
import { some } from '@utils/some';
import moment from 'moment';
import { useEffect } from 'react';
import { Controller, Form, useFormContext } from 'react-hook-form';
import { AvailabilitySlot } from 'shared-types';

import { isOverlapping } from '../../helpers/isTimeslotOverlapping';
import { usePackageConfiguration } from '../../contexts';

import {
  PackagesFormValues,
  ExperienceType,
  AvailabilityForm as AvailabilityFormType,
} from '../../types';
import { TimeSlotType } from 'shared-types/SpaPackage';
import { getSelectedTimeslotsWithStaffAndRoomAllocation } from '../../helpers/getSelectedTimeslotsWithStaffAndRoomAllocation';
import { hasStaffAndRoomAvailability } from '../../helpers/hasStaffAndRoomAvailability';

const TimeSlotOption = (props) => <TimeSlotOptionHOC parentType="Option" {...props} />;
const TimeSlotValue = (props) => <TimeSlotOptionHOC parentType="SingleValue" {...props} />;

export interface AvailabilityFormProps {
  timeslots: TimeSlotType[];
  namePrefix: string;
  id?: number;
  entityType: ExperienceType;
  guestIndex: number;
  delayAfter?: number;
  dayNumber: number;
  experienceIndex: number;
  minimumTimeslotQuantity?: number;
}

type TimeslotOption = AvailabilitySlot & {
  label: string;
  value: Date;
  experienceId: number;
  type: ExperienceType;
  delayAfter: number;
  optionGroupId?: number | string;
  optionId: number | string;
  startTime: string;
  endTime: string;
  visitDate: string;
  quantity: number | null;
  staff?: string[];
  rooms?: string[];
};

export type ArrivalTimeOptionType = TimeslotOption & { dayNumber: number };

export const AvailabilityForm: React.FC<AvailabilityFormProps> = ({
  timeslots,
  id,
  entityType,
  guestIndex,
  delayAfter,
  dayNumber,
  experienceIndex,
  minimumTimeslotQuantity = 1,
}) => {
  const { t } = useTranslations();
  const isDesktop = useIsDesktop();
  const { journeyParams, reservedSpaTimeSlots } = usePackageConfiguration();
  const arrivalTimeName = `experiences.guestOptions.${
    guestIndex || guestIndex === 0 ? guestIndex : 0
  }.timeslots.${dayNumber}.${experienceIndex}` as keyof PackagesFormValues;
  const experienceDate = moment(journeyParams.fromDate).add(dayNumber, 'd').toString();
  const form = useFormContext<PackagesFormValues>();
  const { control, setValue, watch } = form;

  const arrivalTimeNameValue = watch(
    `experiences.guestOptions.${
      guestIndex || guestIndex === 0 ? guestIndex : 0
    }.timeslots.${dayNumber}.${experienceIndex}`
  );
  const guestExperiences = watch(`experiences.guestOptions.${guestIndex}`);
  const allGuestExperiences = watch(`experiences.guestOptions`);
  const selectedExperiences = watch('selectedExperiences');
  const selectedTimeslot = watch(
    `experiences.guestOptions.${guestIndex}.timeslots.${dayNumber}.${experienceIndex}`
  );
  const guestExistingTimeslots = (guestExperiences?.timeslots?.[dayNumber] || []).filter(
    (slot) => slot && slot.experienceId !== arrivalTimeNameValue?.experienceId
  );

  const experienceExistingTimeslots = allGuestExperiences
    ?.filter((_, index) => index !== guestIndex)
    .flatMap((guest) =>
      guest.timeslots?.[dayNumber]?.flatMap((ts) => (ts && ts.experienceId === id ? [ts] : []))
    );

  const selectedTimeslotsWithStaffAndRoomAllocation =
    getSelectedTimeslotsWithStaffAndRoomAllocation(allGuestExperiences);

  const availableTimeOptions =
    (timeslots?.length > 0 &&
      timeslots.filter((time) => !reservedSpaTimeSlots.includes(time?.startTime.toString()))) ||
    [];

  const arrivalTimesOptions = availableTimeOptions.reduce(
    (acc: Array<TimeslotOption & { dayNumber: number }>, timeslot) => {
      const experience = (selectedExperiences || []).find((exp) => Number(exp.id) === Number(id));
      if (experience && id) {
        const experienceDate = moment(journeyParams.fromDate).add(dayNumber, 'd').toString();
        acc.push({
          ...timeslot,
          label: timeslot.startTime,
          delayAfter: delayAfter || 0,
          value: new Date(`${experienceDate} ${timeslot.startTime}`),
          experienceId: id,
          type: entityType,
          optionId: experience.optionId,
          optionGroupId: experience.optionGroupId,
          startDateTime: new Date(timeslot.startTime),
          endDateTime: new Date(timeslot.endTime),
          bandNumber: 0,
          discount: undefined,
          dayNumber,
          visitDate: experienceDate,
          staff: timeslot.staff,
          rooms: timeslot.rooms,
        });
      }
      return acc;
    },
    []
  );

  const hasSameGuestForTimeslot = (timeslot: ArrivalTimeOptionType) =>
    !some(
      guestExistingTimeslots,
      (existingTimeslot) => existingTimeslot && isOverlapping(timeslot, existingTimeslot)
    );

  const hasQuantityAndTimeslotAvailable = (timeslot: ArrivalTimeOptionType) => {
    if (timeslot.quantity !== null && timeslot.quantity < minimumTimeslotQuantity) {
      return false;
    }

    const [timeslotStartHour, timeslotStartMinute] = timeslot.startTime.split(':');

    return !some(experienceExistingTimeslots, (existingTimeslot) => {
      const [existingTimeslotStartHour, existingTimeslotStartMinute] = (
        existingTimeslot?.startTime || ''
      ).split(':');

      const isStillAvailableAfterUserSelection =
        timeslot.quantity && timeslot?.quantity <= experienceExistingTimeslots.length;

      const isAtTheSameTimeAsUserSelection = moment(experienceDate)
        .set({
          hour: Number(existingTimeslotStartHour),
          minute: Number(existingTimeslotStartMinute),
        })
        .isSame(
          moment(experienceDate).set({
            hour: Number(timeslotStartHour),
            minute: Number(timeslotStartMinute),
          })
        );
      return isStillAvailableAfterUserSelection && isAtTheSameTimeAsUserSelection;
    });
  };

  const hasAvailableResources = (timeslot: ArrivalTimeOptionType) => {
    if (timeslot.staff === undefined && timeslot.rooms === undefined) {
      return true;
    }
    return hasStaffAndRoomAvailability(
      timeslot,
      selectedTimeslotsWithStaffAndRoomAllocation,
      guestIndex
    );
  };

  const filteredArrivalTimesOptions = arrivalTimesOptions.filter((timeslot) => {
    return (
      hasSameGuestForTimeslot(timeslot) &&
      hasQuantityAndTimeslotAvailable(timeslot) &&
      hasAvailableResources(timeslot)
    );
  });

  const mappedArrivalTimes = filteredArrivalTimesOptions.map((ts) => {
    const { price: _price, startTime, ...rest } = ts;
    const [hours, minutes] = startTime.split(':');
    return { ...rest, startTime, label: `${hours}:${minutes}` };
  });

  useEffect(() => {
    if (!selectedTimeslot) {
      setValue(
        `experiences.guestOptions.${guestIndex}.timeslots.${dayNumber}.${experienceIndex}`,
        null as unknown as AvailabilityFormType
      );
    }
  }, []);

  const value = mappedArrivalTimes.find((opt) => opt?.startTime === selectedTimeslot?.startTime);

  return (
    <Form>
      <Controller
        control={control}
        name={arrivalTimeName as keyof PackagesFormValues}
        render={({ field: { onChange } }) => {
          return (
            <BlockSelectField
              components={{
                Option: TimeSlotOption,
                SingleValue: TimeSlotValue,
              }}
              options={mappedArrivalTimes}
              onChange={(val) => {
                const selectedTime = arrivalTimesOptions.find((opt) => opt.value === val.value);
                if (selectedTime) {
                  return onChange(selectedTime);
                }
              }}
              isDisabled={!id}
              value={value}
              placeholder={t('aria.optionSelectorDropdownPlaceholder')}
              useNativeDropdownOnMobile={false}
              isSearchable={false}
              menuPlacement={isDesktop ? 'auto' : 'top'}
              noOptionsMessage={() => t('treatment.noTimeslotsAvailable')}
            />
          );
        }}
      />
    </Form>
  );
};
